import { Layers, Plus, Trash2 } from "lucide-react";

import { SearchableSelect } from "@/components/shared/searchable-select";
import { Button } from "@/components/ui/button";
import { NumberInput } from "@/components/ui/number-input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toBaseFactor } from "@/lib/uom-conversion";
import { formatCurrency } from "@/lib/utils";

import type { IngredientRow } from "./use-product-recipe-form";
import type {
  RawMaterial,
  UnitConversion,
  UnitOfMeasurement,
} from "@artisancode/api-types";

interface Props {
  rows: (IngredientRow & { subtotal: number })[];
  rawMaterials: RawMaterial[];
  uoms: UnitOfMeasurement[];
  conversions: UnitConversion[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  onChange: (index: number, field: keyof IngredientRow, value: string) => void;
}

export function ProductRecipeIngredientTable({
  rows,
  rawMaterials,
  uoms,
  conversions,
  onAdd,
  onRemove,
  onChange,
}: Props) {
  return (
    <div className="rounded-lg border p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-foreground text-xs flex items-center gap-1.5">
            <Layers className="h-4 w-4 text-primary" />
            Daftar Bahan Baku Racikan
          </h4>
          <p className="text-[11px] text-muted-foreground">
            Hanya bahan baku (single-level) yang dapat dimasukkan ke dalam
            resep.
          </p>
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onAdd}
          className="h-7 gap-1 text-[11px]"
        >
          <Plus className="h-3.5 w-3.5" />
          Tambah Bahan
        </Button>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40 hover:bg-muted/40">
              <TableHead className="h-8 min-w-56 text-[11px] font-medium">
                Bahan Baku
              </TableHead>
              <TableHead className="h-8 text-[11px] font-medium text-right w-24 min-w-24">
                Jumlah
              </TableHead>
              <TableHead className="h-8 text-[11px] font-medium w-48 min-w-40">
                Satuan
              </TableHead>
              <TableHead className="h-8 text-[11px] font-medium text-right w-28">
                Biaya Bahan
              </TableHead>
              <TableHead className="h-8 w-10"></TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {rows.map((item, idx) => (
              <TableRow key={idx} className="hover:bg-muted/20">
                <TableCell className="py-1.5">
                  <SearchableSelect
                    value={item.rawMaterialId}
                    onChange={(val) => onChange(idx, "rawMaterialId", val)}
                    placeholder="Cari bahan baku..."
                    options={rawMaterials.map((rm) => ({
                      value: rm.id,
                      label: rm.name,
                      hint:
                        rm.costPerUnit == null
                          ? "Belum ada harga"
                          : `@ ${formatCurrency(rm.costPerUnit)} / ${rm.uom?.symbol ?? "unit"}`,
                    }))}
                    className="h-8 text-xs bg-background"
                  />
                  {rawMaterials.find((r) => r.id === item.rawMaterialId)
                    ?.costPerUnit === null && (
                    <span className="mt-0.5 block text-[10px] text-amber-600 dark:text-amber-400">
                      Belum ada harga, HPP belum lengkap
                    </span>
                  )}
                </TableCell>

                <TableCell className="py-1.5 text-right">
                  <NumberInput
                    decimals={4}
                    placeholder="0"
                    value={item.qtyStr ? Number(item.qtyStr) : undefined}
                    onChange={(n) =>
                      onChange(idx, "qtyStr", n ? String(n) : "")
                    }
                    className="h-8 min-w-20 text-xs text-right bg-background"
                  />
                </TableCell>

                <TableCell className="py-1.5">
                  <SearchableSelect
                    value={item.uomId}
                    onChange={(val) => onChange(idx, "uomId", val)}
                    placeholder="Satuan"
                    disabled={!item.rawMaterialId}
                    options={uoms
                      .filter((u) => {
                        const rm = rawMaterials.find(
                          (r) => r.id === item.rawMaterialId,
                        );
                        // the material's own unit + any unit with a conversion rule to it
                        const isApplicable =
                          !!rm &&
                          (u.id === rm.uomId ||
                            toBaseFactor(conversions, u.id, rm.uomId) !== null);
                        return (
                          isApplicable && (u.isActive || u.id === item.uomId)
                        );
                      })
                      .map((u) => ({
                        value: u.id,
                        label: `${u.symbol} (${u.name})${!u.isActive ? " (Nonaktif)" : ""}`,
                      }))}
                    className="h-8 text-xs bg-background"
                  />
                </TableCell>

                <TableCell className="py-1.5 text-right font-medium">
                  {formatCurrency(item.subtotal)}
                </TableCell>

                <TableCell className="py-1.5 text-right pr-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={rows.length <= 1}
                    onClick={() => onRemove(idx)}
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
